/**
 * Single source of truth for every way a visitor can reach the business — and,
 * more importantly, for stamping "this lead came from the website" onto it.
 *
 * The shop also takes walk-ins, calls from printed material, referrals and
 * Instagram DMs, so a WhatsApp message with no provenance is indistinguishable
 * from an offline enquiry. Every WhatsApp link and mailto on the site is
 * therefore built through the helpers below so it always carries the website
 * tag plus the page the visitor was on when they reached out.
 */

export const WHATSAPP_NUMBER = '971505348309';
export const WHATSAPP_DISPLAY = '050 534 8309';
export const PHONE_TEL = '+97142222791';
export const PHONE_DISPLAY = '04 222 2791';
export const EMAIL = 'rasadvertising@gmail.com';
export const INSTAGRAM_HANDLE = '@ras_advertising_gifts';
export const INSTAGRAM_URL = 'https://www.instagram.com/ras_advertising_gifts';
export const MAPS_URL = 'https://maps.google.com/?q=Rashed+Al+Shamsi+Advertising+Gifts';
export const SITE_DOMAIN = 'alshamsiprintuae.com';

/**
 * First line of every website-originated WhatsApp message. It leads rather than
 * trails on purpose: WhatsApp's chat list only previews the opening line, so the
 * tag has to be at the top for the team to spot a web lead without opening it.
 */
const WA_TAG = `🌐 WEBSITE ENQUIRY · ${SITE_DOMAIN}`;

/** Builds the tagged message body a visitor will send over WhatsApp. */
export function waMessage(body: string, source: string): string {
  return `${WA_TAG}\n\n${body}\n\n↳ Page: ${source}`;
}

/** Tagged wa.me link. `source` is the page/CTA the visitor came from. */
export function waLink(body: string, source: string): string {
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessage(body, source))}`;
}

/**
 * Tagged mailto. The subject prefix is what makes the lead filterable in Gmail
 * (a label/filter on "[WEBSITE ENQUIRY]" catches every one of them).
 */
export function mailtoLink(subject: string, source: string, body = ''): string {
  const s = encodeURIComponent(`[WEBSITE ENQUIRY] ${subject}`);
  const b = encodeURIComponent(`${body}\n\n\n---\nSent from ${SITE_DOMAIN} · ${source}`);
  return `mailto:${EMAIL}?subject=${s}&body=${b}`;
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

export type LeadChannel = 'whatsapp' | 'email' | 'phone' | 'form' | 'instagram';

/**
 * Mirrors the lead into GA4 (`G-NB22FB6GMG`, configured in public/index.html).
 * Phone calls are the one channel that cannot carry a tag to the other end, so
 * analytics is the only place the client can count them as website-driven.
 */
export function trackLead(channel: LeadChannel, source: string): void {
  window.gtag?.('event', 'generate_lead', {
    lead_channel: channel,
    lead_source: source,
    page_path: typeof window !== 'undefined' ? window.location.pathname : '',
  });
}
